import * as Cesium from "cesium";
import { includesNormalized, showToast } from "../Utility";
import { computeZoomLevel } from "../MapUtility";
import Type from "../Types";

const PROP_KEY_BY_PARENT = {
  lanelet: "lane_id",
  stopline: "stop_line_id",
  yieldline: "yield_line_id",
  trafficlightbox: "traffic_light_box_id",
  trafficsignbox: "traffic_sign_box_id",
  trafficsignface: "traffic_sign_face_id",
  traficsignbox: "traffic_sign_box_id",
};

export const setupEntityClickHighlight = ({ viewer, canHightlightEntityRef }) => {
  const { ScreenSpaceEventHandler, ScreenSpaceEventType, Entity, Color, ColorMaterialProperty, PointPrimitiveCollection, Cartesian3, Cartographic, PolygonHierarchy, Cartesian2, Matrix4 } = Cesium;

  const OVERLAY_OFFSET_METERS = 0.6;
  const ENDPOINT_SNAP_METERS = 0.002;
  const HEIGHT_DEV_TOL = 0.3;

  const LEVEL_COLORS = [
    Color.ORANGE, // level 1
    Color.fromBytes(255, 255, 0, 255), // level 2 - yellow
    Color.fromBytes(0, 255, 255, 255), // level 3 - cyan
    Color.fromBytes(0, 255, 0, 255), // level 4 - green
    Color.fromBytes(255, 0, 255, 255), // level 5 - magenta
  ];

  function cartoHeight(cart) {
    return Cesium.Cartographic.fromCartesian(cart).height || 0;
  }

  function mean(arr) {
    if (!arr.length) return 0;
    let s = 0;
    for (let i = 0; i < arr.length; i++) s += arr[i];
    return s / arr.length;
  }

  function collectPolygonVertices(entity) {
    const out = [];
    const h = resolve(entity?.polygon?.hierarchy);
    if (!h) return out;
    const stack = [h];
    while (stack.length) {
      const node = stack.pop();
      const pos = node?.positions;
      if (Array.isArray(pos)) out.push(...pos);
      const holes = node?.holes;
      if (Array.isArray(holes)) {
        for (let i = 0; i < holes.length; i++) stack.push(holes[i]);
      }
    }
    return out;
  }

  function collectEntityVertices(entity) {
    const verts = [];
    if (entity.polyline) {
      const p = resolve(entity.polyline.positions);
      if (Array.isArray(p)) verts.push(...p);
    }
    if (entity.polygon) {
      verts.push(...collectPolygonVertices(entity));
    }
    return verts;
  }

  let disposed = false;

  function getEntityProps(entity) {
    const t = viewer?.clock?.currentTime;
    const p = entity?.properties;
    if (!p) return undefined;
    return typeof p.getValue === "function" ? p.getValue(t) : p;
  }

  function readProp(entity, key) {
    const bag = getEntityProps(entity);
    if (!bag) return undefined;
    return bag[key];
  }

  function resolve(prop) {
    if (!prop) return undefined;
    const t = viewer?.clock?.currentTime;
    return typeof prop.getValue === "function" ? prop.getValue(t) : prop;
  }

  function findOwnerDataSource(entity) {
    const dsc = viewer.dataSources;
    for (let i = 0; i < dsc.length; i++) {
      const ds = dsc.get(i);
      if (ds?.entities?.contains && ds.entities.contains(entity)) return ds;
    }
    return null;
  }

  function raiseCartesian(cart, meters) {
    if (!cart) return cart;
    const c = Cartographic.fromCartesian(cart);
    c.height = (c.height || 0) + meters;
    return Cartesian3.fromRadians(c.longitude, c.latitude, c.height);
  }

  function raisePositions(arr, meters) {
    const out = [];
    for (let i = 0; i < arr.length; i++) out.push(raiseCartesian(arr[i], meters));
    return out;
  }

  function raiseHierarchy(node, meters) {
    if (!node) return null;
    const pos = Array.isArray(node.positions) ? raisePositions(node.positions, meters) : [];
    const holes = Array.isArray(node.holes) ? node.holes.map((h) => raiseHierarchy(h, meters)) : [];
    return new PolygonHierarchy(pos, holes);
  }

  function makeOverlayForEntity(target, color) {
    if (disposed) return null;
    const solid = new ColorMaterialProperty(color.withAlpha ? color.withAlpha(0.6) : color);

    if (target.polyline) {
      const positions = resolve(target.polyline.positions);
      if (Array.isArray(positions) && positions.length > 1) {
        const raised = raisePositions(positions, OVERLAY_OFFSET_METERS);
        return viewer.entities.add({
          polyline: {
            positions: raised,
            width: 5.0,
            material: color,
            clampToGround: false,
          },
        });
      }
    }

    if (target.polygon) {
      const hierarchy = resolve(target.polygon.hierarchy);
      if (hierarchy) {
        const raised = raiseHierarchy(hierarchy, OVERLAY_OFFSET_METERS);
        return viewer.entities.add({
          polygon: {
            hierarchy: raised,
            perPositionHeight: true,
            material: solid,
            outline: true,
            outlineColor: Color.BLACK,
            outlineWidth: 3.0,
          },
        });
      }
    }

    if (target.point) {
      return viewer.entities.add({
        position: raiseCartesian(resolve(target.position), OVERLAY_OFFSET_METERS),
        point: {
          color,
          pixelSize: 15,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
    }

    return null;
  }

  function collectAllEntities() {
    const out = [];
    const dsc = viewer.dataSources;
    const n = dsc.length;
    for (let i = 0; i < n; i++) {
      const ds = dsc.get(i);
      const values = ds?.entities?.values || [];
      for (let j = 0; j < values.length; j++) {
        const e = values[j];
        if (e instanceof Entity) out.push(e);
      }
    }
    return out;
  }

  function csvToIdSet(val) {
    const out = new Set();
    if (val == null) return out;
    if (Array.isArray(val)) {
      for (let k = 0; k < val.length; k++) {
        String(val[k])
          .split(",")
          .forEach((tok) => {
            tok = tok.trim();
            if (tok) out.add(tok);
          });
      }
    } else {
      String(val)
        .split(",")
        .forEach((tok) => {
          tok = tok.trim();
          if (tok) out.add(tok);
        });
    }
    return out;
  }

  function buildIdIndex() {
    const index = new Map();
    const all = collectAllEntities();
    for (let i = 0; i < all.length; i++) {
      const e = all[i];
      const idProp = readProp(e, "id");
      if (idProp == null) continue;
      const key = String(idProp);
      let arr = index.get(key);
      if (!arr) {
        arr = [];
        index.set(key, arr);
      }
      arr.push(e);
    }
    return index;
  }

  let vertexLayer = viewer.scene.primitives.add(new PointPrimitiveCollection());
  let overlayPoints = [];

  function ensureVertexLayer() {
    if (!vertexLayer || (typeof vertexLayer.isDestroyed === "function" && vertexLayer.isDestroyed())) {
      vertexLayer = viewer.scene.primitives.add(new PointPrimitiveCollection());
    }
  }

  function addVertexMarkers(target) {
    if (disposed) return;
    ensureVertexLayer();

    const verts = collectEntityVertices(target);
    if (!verts.length) return;

    const heights = verts.map(cartoHeight);
    const refH = mean(heights);

    for (let i = 0; i < verts.length; i++) {
      const h = heights[i];
      const isPurple = Math.abs(h - refH) > HEIGHT_DEV_TOL;
      const color = isPurple ? Color.PURPLE : Color.WHITE;
      const size = isPurple ? 8 : 6;

      const p = vertexLayer.add({
        position: raiseCartesian(verts[i], OVERLAY_OFFSET_METERS),
        pixelSize: size,
        color,
        outlineColor: Color.BLACK,
        outlineWidth: 1,
        disableDepthTestDistance: Number.POSITIVE_INFINITY,
      });
      overlayPoints.push(p);
    }
  }

  function clearVertexMarkers() {
    if (!vertexLayer || (typeof vertexLayer.isDestroyed === "function" && vertexLayer.isDestroyed())) {
      overlayPoints = [];
      return;
    }
    for (let i = 0; i < overlayPoints.length; i++) {
      try {
        vertexLayer.remove(overlayPoints[i]);
      } catch {}
    }
    overlayPoints = [];
  }

  const handler = new ScreenSpaceEventHandler(viewer.scene.canvas);
  let overlayEntities = [];
  let currentBaseEntity = null;

  function clearShapeOverlays() {
    for (let i = 0; i < overlayEntities.length; i++) {
      try {
        viewer.entities.remove(overlayEntities[i]);
      } catch {}
    }
    overlayEntities = [];
  }

  function clearOverlays() {
    clearShapeOverlays();
    clearVertexMarkers();
  }

  function getPolylineEndpoints(ent) {
    if (!ent?.polyline) return [null, null];
    const pos = resolve(ent.polyline.positions);
    if (!Array.isArray(pos) || pos.length < 2) return [null, null];
    return [pos[0], pos[pos.length - 1]];
  }

  function dist2D(a, b) {
    if (!a || !b) return Number.POSITIVE_INFINITY;
    const ca = Cartographic.fromCartesian(a);
    const cb = Cartographic.fromCartesian(b);
    const g = new Cesium.EllipsoidGeodesic(ca, cb);
    return g.surfaceDistance;
  }

  function findNextEntity(cur) {
    const [_, e] = getPolylineEndpoints(cur);
    if (!e) return null;
    const all = collectAllEntities();
    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (let i = 0; i < all.length; i++) {
      const cand = all[i];
      if (cand === cur || !cand.polyline) continue;
      const [cs, ce] = getPolylineEndpoints(cand);
      if (!cs || !ce) continue;

      const dStart = dist2D(e, cs);
      const dEnd = dist2D(e, ce);

      const score = Math.min(dStart <= ENDPOINT_SNAP_METERS ? dStart : Number.POSITIVE_INFINITY, dEnd <= ENDPOINT_SNAP_METERS ? dEnd : Number.POSITIVE_INFINITY);

      if (score < bestScore) {
        bestScore = score;
        best = cand;
      }
    }
    return Number.isFinite(bestScore) ? best : null;
  }

  function findPrevEntity(cur) {
    const [s, _] = getPolylineEndpoints(cur);
    if (!s) return null;
    const all = collectAllEntities();
    let best = null;
    let bestScore = Number.POSITIVE_INFINITY;

    for (let i = 0; i < all.length; i++) {
      const cand = all[i];
      if (cand === cur || !cand.polyline) continue;
      const [cs, ce] = getPolylineEndpoints(cand);
      if (!cs || !ce) continue;

      const dEnd = dist2D(ce, s);
      const dStart = dist2D(cs, s);

      const score = Math.min(dEnd <= ENDPOINT_SNAP_METERS ? dEnd : Number.POSITIVE_INFINITY, dStart <= ENDPOINT_SNAP_METERS ? dStart : Number.POSITIVE_INFINITY);

      if (score < bestScore) {
        bestScore = score;
        best = cand;
      }
    }
    return Number.isFinite(bestScore) ? best : null;
  }

  function applyNonOsmSelection(baseEntity, owner, already) {
    const isLanelet = includesNormalized(owner?.layerName, "lanelet");
    let rightEnt = null;
    let leftEnt = null;

    if (isLanelet) {
      const readSide = (key) => readProp(baseEntity, key);

      const rightId = String(readSide("right_line_id") ?? "");
      const leftId = String(readSide("left_line_id") ?? "");
      const rightType = String(readSide("right_line_type") ?? "");
      const leftType = String(readSide("left_line_type") ?? "");

      function findByIdInTypedSources(typeStr, targetId) {
        if (!typeStr || !targetId) return null;
        const dsCol = viewer.dataSources;
        for (let i = 0; i < dsCol.length; i++) {
          const ds = dsCol.get(i);
          if (!includesNormalized(ds?.layerName, typeStr)) continue;
          const values = ds?.entities?.values || [];
          for (let j = 0; j < values.length; j++) {
            const e = values[j];
            const idProp = readProp(e, "id");
            if (idProp != null && String(idProp) === String(targetId)) return e;
          }
        }
        return null;
      }

      rightEnt = findByIdInTypedSources(rightType, rightId);
      if (rightEnt) {
        const ov = makeOverlayForEntity(rightEnt, Color.BLUE);
        if (ov) overlayEntities.push(ov);
        addVertexMarkers(rightEnt);
        already.add(rightEnt.id);
      }

      leftEnt = findByIdInTypedSources(leftType, leftId);
      if (leftEnt) {
        const ov = makeOverlayForEntity(leftEnt, Color.PURPLE);
        if (ov) overlayEntities.push(ov);
        addVertexMarkers(leftEnt);
        already.add(leftEnt.id);
      }
    }

    const parentKeys = Object.values(PROP_KEY_BY_PARENT);
    const idIndex = buildIdIndex();
    const visited = new Set(already);
    const queue = [{ ent: baseEntity, depth: 0 }];

    while (queue.length) {
      const { ent, depth } = queue.shift();
      if (!ent) continue;

      const targetIds = new Set();
      for (let i = 0; i < parentKeys.length; i++) {
        const key = parentKeys[i];
        const v = readProp(ent, key);
        if (v == null) continue;
        const ids = csvToIdSet(v);
        ids.forEach((id) => targetIds.add(id));
      }
      if (targetIds.size === 0) continue;

      const nextDepth = depth + 1;
      const color = LEVEL_COLORS[Math.min(nextDepth - 1, LEVEL_COLORS.length - 1)];

      targetIds.forEach((idStr) => {
        const related = idIndex.get(idStr);
        if (!related) return;

        for (let k = 0; k < related.length; k++) {
          const e = related[k];
          if (!e || visited.has(e.id)) continue;

          visited.add(e.id);

          const ov = makeOverlayForEntity(e, color);
          if (ov) overlayEntities.push(ov);
          addVertexMarkers(e);

          queue.push({ ent: e, depth: nextDepth });
        }
      });
    }
  }

  function collectPositionsFromOverlays() {
    const pts = [];
    for (let i = 0; i < overlayEntities.length; i++) {
      const o = overlayEntities[i];
      if (o.polyline) {
        const pos = resolve(o.polyline.positions);
        if (Array.isArray(pos)) pts.push(...pos);
      } else if (o.polygon) {
        const h = resolve(o.polygon.hierarchy);
        if (!h) continue;
        const stack = [h];
        while (stack.length) {
          const node = stack.pop();
          const p = node?.positions;
          if (Array.isArray(p)) pts.push(...p);
          const holes = node?.holes;
          if (Array.isArray(holes)) {
            for (let k = 0; k < holes.length; k++) stack.push(holes[k]);
          }
        }
      } else if (o.position) {
        const p = resolve(o.position);
        if (p) pts.push(p);
      }
    }
    return pts;
  }

  function panCameraToOverlays() {
    if (disposed) return;
    const pts = collectPositionsFromOverlays();
    if (pts.length === 0) return;

    let sumLon = 0,
      sumLat = 0;
    for (let i = 0; i < pts.length; i++) {
      const c = Cartographic.fromCartesian(pts[i]);
      sumLon += c.longitude;
      sumLat += c.latitude;
    }
    const lon = sumLon / pts.length;
    const lat = sumLat / pts.length;

    const scene = viewer.scene;
    const cam = scene.camera;
    const ellipsoid = scene.globe.ellipsoid;
    const canvas = scene.canvas;

    const centerPx = new Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
    const ray = cam.getPickRay(centerPx);
    let oldCenter = scene.globe.pick(ray, scene) || cam.pickEllipsoid(centerPx, ellipsoid);
    if (!oldCenter) return;

    const oldCarto = Cartographic.fromCartesian(oldCenter);
    const targetPoint = Cartesian3.fromRadians(lon, lat, oldCarto.height);

    const delta = Cartesian3.subtract(targetPoint, oldCenter, new Cartesian3());
    cam.lookAtTransform(Matrix4.IDENTITY);
    cam.position = Cartesian3.add(cam.position, delta, new Cartesian3());
  }

  function applySelection(baseEntity, pan = false) {
    if (disposed) return;
    clearOverlays();

    if (!baseEntity) {
      currentBaseEntity = null;
      viewer.scene.requestRender?.();
      return;
    }

    if (computeZoomLevel(viewer) < 12) {
      showToast("You must be at zoom level 12 or higher to see details and relationships.", Type.NOTIFICATION.WARNING);
      return;
    }

    const owner = findOwnerDataSource(baseEntity);

    const baseColor = Color.RED;
    const baseOv = makeOverlayForEntity(baseEntity, baseColor);
    if (baseOv) overlayEntities.push(baseOv);
    addVertexMarkers(baseEntity);

    const already = new Set([baseEntity.id]);

    applyNonOsmSelection(baseEntity, owner, already);

    currentBaseEntity = baseEntity;
    if (pan) panCameraToOverlays();
    viewer.scene.requestRender?.();
  }

  function onLeftClick(click) {
    if (disposed) return;
    if (!canHightlightEntityRef?.current) return;

    let picked;
    try {
      picked = viewer.scene.pick(click.position);
    } catch {
      picked = null;
    }
    const pickedEntity = picked?.id instanceof Entity ? picked.id : picked?.object?.id instanceof Entity ? picked.object.id : picked?.primitive?.id instanceof Entity ? picked.primitive.id : null;

    if (overlayEntities.includes(picked?.id) || overlayPoints.includes(picked?.primitive)) {
      return;
    }

    applySelection(pickedEntity || null, false);
  }

  handler.setInputAction(onLeftClick, ScreenSpaceEventType.LEFT_CLICK);

  function onKeyDown(e) {
    if (disposed) return;
    if (!e.shiftKey) return;

    const isNext = e.key === ">" || e.code === "Period";
    const isPrev = e.key === "<" || e.code === "Comma";
    if (!isNext && !isPrev) return;
    if (!currentBaseEntity) return;

    if (isNext) {
      const next = findNextEntity(currentBaseEntity);
      if (next) applySelection(next, true);
    } else if (isPrev) {
      const prev = findPrevEntity(currentBaseEntity);
      if (prev) applySelection(prev, true);
    }
  }

  window.addEventListener("keydown", onKeyDown, { passive: true });

  function clearHighlightsOnly() {
    if (disposed) return;
    clearOverlays();
    viewer.scene.requestRender?.();
  }

  return {
    handler,
    clearHighlights: clearHighlightsOnly,
    cleanup: () => {
      disposed = true;
      clearOverlays();
      try {
        handler.destroy();
      } catch {}
      try {
        viewer.scene.primitives.remove(vertexLayer);
      } catch {}
      window.removeEventListener("keydown", onKeyDown);
      currentBaseEntity = null;
    },
  };
};
